"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "motion/react";
import { MediaCarousel } from "@/components/MediaCarousel";

export type CentreCard = {
  id: string;
  slug: string | null;
  name: string;
  microMarket: string | null;
  image: string | null;
  /** The centre's gallery, so the tile carries the same carousel and lightbox
   *  as the centre page — a reader can see the building before clicking. */
  gallery: string[];
  /**
   * Workspace solutions offered here — "Custom-Built", "Suites", or both.
   * Resolved server-side by centreSolutions() (DB column, else the copy).
   */
  solutions: string[];
};

const EASE = [0.16, 1, 0.3, 1] as const;

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`mono-meta rounded-full border px-4 py-2 transition-colors ${
        active
          ? "border-brand-accent bg-brand-accent text-white"
          : "border-brand-line text-brand-slate hover:border-brand-accent hover:text-brand-accent-dark"
      }`}
    >
      {label}
    </button>
  );
}

// Filterable centres grid — filter by micro-market ("Areas") and by
// workspace solution ("Solutions": Managed / Suites).
export function CentresGrid({
  centres,
  citySlug,
  cityName,
}: {
  centres: CentreCard[];
  citySlug: string;
  cityName: string;
}) {
  const [area, setArea] = useState<string | null>(null);
  const [solution, setSolution] = useState<string | null>(null);

  const areas = useMemo(
    () =>
      [...new Set(centres.map((c) => c.microMarket).filter(Boolean))] as string[],
    [centres],
  );
  // Chip order follows the canonical Custom-Built → Suites order rather than
  // whichever centre happens to come first in the grid.
  const solutions = useMemo(() => {
    const found = new Set(centres.flatMap((c) => c.solutions));
    return ["Custom-Built", "Suites"].filter((s) => found.delete(s)).concat([...found]);
  }, [centres]);

  // A centre offering both matches either filter.
  const filtered = centres.filter(
    (c) =>
      (!area || c.microMarket === area) &&
      (!solution || c.solutions.includes(solution)),
  );

  return (
    <>
      {/* filters */}
      <div className="mt-10 flex flex-col gap-5 md:mt-12">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="mono-meta w-24 shrink-0 uppercase text-brand-muted">
            Areas
          </span>
          <Chip label="All" active={!area} onClick={() => setArea(null)} />
          {areas.map((a) => (
            <Chip
              key={a}
              label={a}
              active={area === a}
              onClick={() => setArea(area === a ? null : a)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="mono-meta w-24 shrink-0 uppercase text-brand-muted">
            Solutions
          </span>
          <Chip
            label="All"
            active={!solution}
            onClick={() => setSolution(null)}
          />
          {solutions.map((s) => (
            <Chip
              key={s}
              label={s}
              active={solution === s}
              onClick={() => setSolution(solution === s ? null : s)}
            />
          ))}
        </div>
      </div>

      {/* grid */}
      <motion.div layout className="mt-12 grid gap-x-4 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {filtered.map((centre) => {
            // The tile is a doorway to the centre: the pictures preview it
            // and a click on any of them goes there, the same as the title.
            // The carousel's arrows and dots are buttons beside that link,
            // never inside it — a button inside an <a> is invalid HTML that
            // navigates on every press. "group" sits on the card so hover
            // anywhere lifts the picture and lights the "View centre" line.
            const media = [centre.image, ...centre.gallery].filter(
              (v, i, a): v is string => !!v && a.indexOf(v) === i,
            );
            const text = (
              <div className="mt-4">
                <h3 className="text-base font-medium text-brand-ink">
                  {centre.name}
                </h3>
                <div className="mono-meta mt-1 text-brand-muted">
                  {(centre.microMarket ?? "MANAGED CENTRE").toUpperCase()} ·{" "}
                  {centre.solutions.join(" + ").toUpperCase()}
                </div>
                {centre.slug && (
                  <span className="mt-1 block text-sm text-brand-accent-dark opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    View centre →
                  </span>
                )}
              </div>
            );
            return (
              <motion.div
                key={centre.id}
                layout
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="group"
              >
                {media.length > 0 ? (
                  <MediaCarousel
                    items={media}
                    alt={`${centre.name} — Table Space centre in ${cityName}`}
                    href={centre.slug ? `/locations/${citySlug}/${centre.slug}` : undefined}
                    className="aspect-[4/3] w-full overflow-hidden rounded-2xl ring-1 ring-brand-line"
                  />
                ) : (
                  <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl ring-1 ring-brand-line" />
                )}
                {centre.slug ? (
                  <Link href={`/locations/${citySlug}/${centre.slug}`} className="block">
                    {text}
                  </Link>
                ) : (
                  text
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {filtered.length === 0 && (
        <p className="mt-12 text-base font-normal leading-relaxed text-brand-slate">
          No centres match the selected filters — clear a filter to see more.
        </p>
      )}
    </>
  );
}
